// LedgerManagement.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    Spinner,
    UncontrolledTooltip,
    Table,
} from "reactstrap";
import {
    FaFileInvoice,
    FaDownload,
    FaEye,
    FaEdit,
    FaCheck,
    FaExclamationTriangle,
    FaLock,
    FaFileExport,
    FaFilePdf,
    FaFileExcel,
    FaHistory,
    FaPlus,
    FaMinus,
    FaClock,
    FaArrowLeft,
    FaArrowRight,
} from "react-icons/fa";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactBsTable from "components/Tables/ReactBsTable";
import NotificationAlert from "react-notification-alert";
import { MAX_FILE_SIZE } from "variables/constants";
import {
    listPublisherForLedger,
    listPublisherForPostedLedger,
} from "actions/publisher";
import { addAdjustmentData } from "actions/adjustment";
import { getAdjustmentDetails } from "actions/adjustment";
import { postLedger } from "actions/ledger";
import { updateLedger, uploadInvoice } from "actions/ledger";
import { showAlert } from "components/Common/commonFunctions";
import SearchableDropdown from "components/DropDown/SearchableDropdown";
import RerunLogs from "./RerunLogs";
import { downloadInvoice } from "actions/ledger";
import { updateInvoiceStatus } from "actions/ledger";

// =============== MODAL STYLES ===============
const modalStyles = {
    header: {
        borderBottom: "2px solid #f0f0f0",
        paddingBottom: "18px",
        paddingTop: "18px",
    },
    title: {
        fontSize: "18px",
        fontWeight: "700",
        color: "#2c3e50",
        letterSpacing: "0.3px",
    },
    body: {
        padding: "24px",
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
    },
};

const getMonthsWithYear = (year = new Date().getFullYear()) => {
    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    return monthNames.map((month, index) => ({
        value: String(index + 1),
        label: `${month} ${year}`
    }));
};

// =============== COMPONENT ===============
const LedgerManagement = () => {
    const notificationAlertRef = useRef(null);

    // Get initial month from localStorage or use current month
    const getInitialMonth = () => {
        try {
            const saved = localStorage.getItem("ledger_selected_month");
            if (saved) {
                const month = parseInt(saved);
                if (month >= 1 && month <= 12) {
                    return String(month);
                }
            }
        } catch (e) {
            console.warn("Failed to read saved month:", e);
        }
        return String(new Date().getMonth() + 1);
    };

    const [selectedMonth, setSelectedMonth] = useState(getInitialMonth);
    const [selectedPublisher, setSelectedPublisher] = useState("");
    const [publisherOptions, setPublisherOptions] = useState([]);
    const [selectedLedger, setSelectedLedger] = useState(null);
    const [apiLedgers, setApiLedgers] = useState([]);
    const [filteredLedgers, setFilteredLedgers] = useState([]);
    const [hasRerun, setHasRerun] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPublishers, setSelectedPublishers] = useState([]);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [reason, setReason] = useState("");
    const [showPostLedger, setShowPostLedger] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPostedRecords, setTotalPostedRecords] = useState(0);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const MONTHS = useMemo(() => getMonthsWithYear(currentYear), [currentYear]);
    const [publisherDetails, setPublisherDetails] = useState({
        ID: 0,
        name: "",
        month: "",
        monthValue: 0,
    });
    const [rerunErrors, setRerunErrors] = useState({});

    // Modal states
    const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
    const [isRerunModalOpen, setIsRerunModalOpen] = useState(false);
    const [showRerunLogModal, setShowRerunLogModal] = useState(false);
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [isLedgerManagementModal, setIsLedgerManagementModal] = useState(false);

    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);
    const [isUploadInvoiceModalOpen, setIsUploadInvoiceModalOpen] = useState(false);

    // Adjustment form state
    const [adjustmentForm, setAdjustmentForm] = useState({
        type: "CREDIT",
        amount: "",
        description: "",
        reference: ""
    });
    const [adjustmentErrors, setAdjustmentErrors] = useState({});
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingAdjustmentId, setEditingAdjustmentId] = useState(null);

    const [advanceForm, setAdvanceForm] = useState({
        amount: "",
        currency: "USD",
        date: new Date(),
        description: ""
    });

    const [uploadInvoiceForm, setUploadInvoiceForm] = useState({
        publisherName: "",
        monthDisplay: "",
        invoiceNumber: "",
        invoiceFile: null,
        error: null,
    });

    // Filter
    const [filterText, setFilterText] = useState("");
    const [availableYears, setAvailableYears] = useState([]);

    const monthPickerStyles = `
        .styled-month-input {
            border: 1px solid #1336a0 !important;
            border-radius: 12px !important;
            height: 44px !important;
            padding: 0.75rem 0.9rem !important;
            font-size: 0.95rem !important;
            background: linear-gradient(180deg, #ff0000 0%, #f8faff 100%) !important;
            box-shadow: 0 2px 8px rgba(185, 185, 185, 0.08) !important;
            transition: all 0.2s ease !important;
        }
        .styled-month-input:focus {
            border-color: #b7b7b7 !important;
            box-shadow: 0 0 0 4px rgba(108, 108, 109, 0.12) !important;
            outline: none !important;
        }
        .styled-month-picker {
            border: none !important;
            border-radius: 16px !important;
            box-shadow: 0 18px 45px rgba(15, 23, 42, 0.16) !important;
            overflow: hidden !important;
            background: #ffffff !important;
        }
        .styled-month-picker .react-datepicker__header {
            background: linear-gradient(135deg, #3b3a3a 0%, #8a8a8a 100%) !important;
            border-bottom: none !important;
            padding-top: 14px !important;
        }
        .styled-month-picker .react-datepicker__current-month,
        .styled-month-picker .react-datepicker-time__header,
        .styled-month-picker .react-datepicker-year-header {
            color: #ffffff !important;
            font-weight: 700 !important;
        }
        .styled-month-picker .react-datepicker__navigation {
            top: 7px !important;
               align-items: center !important;
              
        }
        .styled-month-picker .react-datepicker__navigation-icon::before {
            border-color: #ffffff !important;
               align-items: center !important;
              
        }
        .styled-month-picker .react-datepicker__month-text {
            width: 88px !important;
            padding: 12px 0 !important;
            border-radius: 10px !important;
            margin: 5px 0 !important;
            font-size: 0.9rem !important;
            color: #243b53 !important;
        }
        .styled-month-picker .react-datepicker__month-text:hover {
            background: #cdcdcd !important;
            color: #121212 !important;
        }
        .styled-month-picker .react-datepicker__month-text--selected,
        .styled-month-picker .react-datepicker__month-text--keyboard-selected {
            background: linear-gradient(135deg, #a2a2a2 0%, #1a1818 100%) !important;
            color: #ffffff !important;
            box-shadow: 0 8px 18px rgba(143, 143, 143, 0.22) !important;
        }
        .styled-month-picker-popper {
            z-index: 1300 !important;
        }
    `;

    // Get user data from localStorage
    const userData = JSON.parse(localStorage.getItem("p"));
    const currentUser = userData?.email || "Admin";

    // Save month to localStorage whenever it changes
    useEffect(() => {
        try {
            if (selectedMonth) {
                localStorage.setItem("ledger_selected_month", selectedMonth);
            } else {
                localStorage.removeItem("ledger_selected_month");
            }
        } catch (e) {
            console.warn("Failed to save month:", e);
        }
    }, [selectedMonth]);

    // =============== TRUNCATE TEXT HELPER ===============
    const truncateText = (text, maxLength = 30) => {
        if (!text) return "";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    };

    // Helper function to get month display name
    const getMonthDisplay = (monthNumber, year) => {
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        const currentYear = year || new Date().getFullYear();
        return `${monthNames[monthNumber - 1]} ${currentYear}`;
    };

    const getDocTypeFromName = (name = "") => {
        const ext = name.split(".").pop()?.toLowerCase();
        if (ext === "pdf") return "pdf";
        if (ext === "doc" || ext === "docx") return "doc";
        if (ext === "png" || ext === "jpg" || ext === "jpeg" || ext === "gif") return "image";
        return "unknown";
    };

    // =============== FETCH LEDGER DATA (API) ===============
    const fetchLedgerData = useCallback(async (page, month, year, isPostedView = showPostLedger, publisherId = selectedPublisher) => {
        try {
            setIsLoading(true);
            // ✅ Clear previous data immediately so stale rows don't show
            setApiLedgers([]);
            setFilteredLedgers([]);
            if (publisherId) {
                const currentMonth = new Date().getMonth() + 1;
                setSelectedMonth(currentMonth)
            }
            // Determine the target month and year based on filters
            let targetMonth = month;
            let targetYear = year;

            // If publisher is selected, don't pass month/year to get all months
            if (publisherId && publisherId !== "") {
                targetMonth = null;
                targetYear = null;
            } else {
                // If month is not provided, use selected month
                if (targetMonth === null || targetMonth === undefined) {
                    targetMonth = selectedMonth ? parseInt(selectedMonth) : null;
                }
                if (targetYear === null || targetYear === undefined) {
                    targetYear = selectedMonth ? currentYear : null;
                }
            }

            const ledgerApiCall = isPostedView ? listPublisherForLedger : listPublisherForPostedLedger;

            const response = await ledgerApiCall(0, targetMonth, targetYear, publisherId || 0);

            if (response?.code === 200 && response?.data) {
                const data = response.data;
                setHasRerun(data.hasRerun)
                const publishers = Array.isArray(data.publishers)
                    ? data.publishers
                    : Array.isArray(data.ledgers)
                        ? data.ledgers
                        : [];

                const mappedLedgers = publishers.map((item) => {
                    // Get month from the data or use the passed month
                    let monthKey = item.month || '';
                    let monthDisplay = item.monthDisplay || item.ledgerMonth;

                    // If month is not in data and we have a selected month, use it
                    if (!monthKey && targetMonth && targetYear) {
                        monthKey = `${targetYear}-${String(targetMonth).padStart(2, "0")}`;
                        const monthDate = new Date(targetYear, targetMonth - 1, 1);
                        monthDisplay = format(monthDate, "MMMM yyyy");
                    }

                    // If publisher is selected and month is not in data, use the current month from the item
                    if (!monthKey && item.month) {
                        monthKey = item.month;
                    }

                    const adjustment = item.adjustment || {};
                    const adjustmentType = adjustment.adjustmentType || item.adjustmentType || "";
                    const adjustmentAmount = Number(adjustment.amount ?? item.adjustments ?? 0);
                    const adjustmentID = adjustment.ID || item.adjustmentID || 0;
                    const adjustmentDescription = adjustment.description || item.adjustmentDescription || "";
                    const exactPayout = Number(item.netPayable ?? item.totalPayout ?? 0) - Number(item.advanceApplied ?? 0);
                    const netPayable = Number(item.netPayable ?? item.totalPayout ?? 0);
                    const status = item.status || (isPostedView ? "POSTED" : "open");
                    const invoice = item.invoice || {};

                    return {
                        id: item.id || `${item.publisherId || item.companyName || "publisher"}-${monthKey}`,
                        publisherId: item.publisherId || item.id,
                        ledgerID: item.ledgerID || item.id,
                        adjustmentID: adjustmentID,
                        totalPayout: exactPayout || 0,
                        publisherName: item.companyName || item.publisherName || item.name || `Publisher ${item.id}`,
                        month: monthKey,
                        monthDisplay: monthDisplay,
                        postedAt: item.postedAt,
                        ledgerMonth: item.ledgerMonth,
                        revenue: Number(item.totalPayout ?? item.revenue ?? 0),
                        adjustments: adjustmentAmount,
                        adjustmentType: adjustmentType,
                        adjustmentDescription: adjustmentDescription,
                        adjustmentsList: adjustmentID > 0 ? [{
                            id: adjustmentID,
                            type: adjustmentType,
                            amount: adjustmentAmount,
                            description: adjustmentDescription,
                            createdAt: new Date().toISOString(),
                            user: "Admin"
                        }] : [],
                        advanceApplied: Number(item.advanceApplied ?? 0),
                        netPayable: netPayable,
                        status: status,
                        invoiceNumber: invoice.invoiceNumber || item.invoiceNumber || null,
                        invoiceDate: invoice.issueDate || item.invoiceDate || null,
                        dueDate: invoice.dueDate || item.dueDate || null,
                        pdfUrl: invoice.invoicePath || item.pdfUrl || null,
                        isLocked: false,

                        postedBy: null,
                        country: item.country || "",
                        currency: item.currency || "USD",
                        paymentTerms: item.paymentTerms || "Net 30",
                        invoiceId: invoice.id || item.invoiceId || 0,
                        invoiceStatus: invoice.status || item.invoiceStatus || null,
                        invoicePath: invoice.invoicePath || item.invoicePath || null,
                        issueDate: invoice.issueDate || item.issueDate || null,
                    };
                }) || [];
                setApiLedgers(mappedLedgers);
                setTotalRecords(data.totalRecords || mappedLedgers.length);
                setTotalPostedRecords(data.totalPostedRecord || 0);


            } else {
                setApiLedgers([]);
                setTotalRecords(0);
                setTotalPostedRecords(0);
                notify("danger", response?.message || "No ledger data found");
            }
        } catch (error) {
            console.error("Error fetching ledger data:", error);
            setApiLedgers([]);
            setTotalRecords(0);
            setTotalPostedRecords(0);
            notify("danger", "Failed to fetch ledger data");
        } finally {
            setIsLoading(false);
        }
    }, [selectedMonth, selectedPublisher, currentYear, showPostLedger]);

    // =============== EFFECTS ===============
    useEffect(() => {
        const year = new Date().getFullYear();
        setAvailableYears([year, year - 1, year - 2]);
        fetchLedgerData(pageNum);
    }, []);

    // load publisher options for filter
    useEffect(() => {
        const loadPublishers = async () => {
            try {
                const res = await (await import("actions/publisher")).getPublisherList(0, [], "", "", "");
                const pubs = res?.data?.Publisher || res?.data || [];
                const opts = (pubs || []).map(p => ({ value: p.ID || p.id, label: p.companyName || p.name }));
                setPublisherOptions(opts);
            } catch (e) {
                console.warn("Failed to load publishers for filter", e);
            }
        };
        loadPublishers();
    }, []);

    useEffect(() => {
        filterLedgers();
    }, [apiLedgers, selectedMonth, filterText, showPostLedger, currentYear, selectedPublisher]);

    const filterLedgers = useCallback(() => {
        let filtered = [...apiLedgers];

        // Filter by month if month is selected and no publisher filter
        if (selectedMonth && !selectedPublisher) {
            const monthKey = `${currentYear}-${String(selectedMonth).padStart(2, "0")}`;
            filtered = filtered.filter(l => l.month === monthKey);
        }

        // Filter by status for posted view
        if (!showPostLedger) {
            filtered = filtered.filter(l => {
                const status = (l.status || "open").toUpperCase();
                return status !== "OPEN" && status !== "DRAFT";
            });
        }

        // Filter by publisher name text
        if (filterText) {
            filtered = filtered.filter(l =>
                (l.publisherName || "").toLowerCase().includes(filterText.toLowerCase())
            );
        }

        setFilteredLedgers(filtered);
    }, [apiLedgers, selectedMonth, filterText, showPostLedger, currentYear, selectedPublisher]);

    // =============== NOTIFICATIONS ===============
    const notify = (type, message) => {
        if (notificationAlertRef.current) {
            notificationAlertRef.current.notificationAlert({
                place: "tr",
                message: <div>{message}</div>,
                type,
                icon: "tim-icons icon-bell-55",
                autoDismiss: 5,
            });
        }
    };

    // =============== VALIDATE ADJUSTMENT FORM ===============
    const validateAdjustmentForm = () => {
        const errors = {};

        if (!adjustmentForm.type) {
            errors.type = "Adjustment type is required";
        }
        if (!adjustmentForm.amount || parseFloat(adjustmentForm.amount) <= 0) {
            errors.amount = "Amount must be greater than 0";
        }
        if (!adjustmentForm.description || adjustmentForm.description.trim() === "") {
            errors.description = "Description is required";
        }

        setAdjustmentErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const getFirstDayOfMonth = (monthNumber, year = new Date().getFullYear()) => {
        const date = new Date(year, monthNumber - 1, 1);
        const yearStr = date.getFullYear();
        const monthStr = String(date.getMonth() + 1).padStart(2, '0');
        const dayStr = String(date.getDate()).padStart(2, '0');
        return `${yearStr}-${monthStr}-${dayStr}`;
    };

    // =============== RESET ADJUSTMENT FORM ===============
    const resetAdjustmentForm = () => {
        setAdjustmentForm({ type: "CREDIT", amount: "", description: "", reference: "" });
        setAdjustmentErrors({});
        setIsEditMode(false);
        setEditingAdjustmentId(null);
        setPublisherDetails({
            ID: 0,
            name: "",
            month: "",
            monthValue: 0,
        });
    };

    // =============== FETCH ADJUSTMENT DETAILS ===============
    const fetchAdjustmentDetails = async (adjustmentId) => {
        if (!adjustmentId || adjustmentId === 0) {
            return false;
        }

        try {
            setIsLoading(true);
            const response = await getAdjustmentDetails(adjustmentId);
            if (response?.code === 200 && response?.data) {
                const data = response.data;
                setAdjustmentForm({
                    type: data.adjustmentType || "CREDIT",
                    amount: data.amount?.toString() || "",
                    description: data.description || "",
                    reference: data.reference || ""
                });
                setIsEditMode(true);
                setEditingAdjustmentId(adjustmentId);
                return true;
            } else {
                notify("error", response?.message || "Failed to fetch adjustment details");
                return false;
            }
        } catch (error) {
            console.error("Error fetching adjustment details:", error);
            notify("danger", "Failed to fetch adjustment details");
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    // =============== ADD ADJUSTMENT API CALL ===============
    const handleAddAdjustment = async () => {
        if (!validateAdjustmentForm()) {
            return;
        }

        try {
            setIsLoading(true);

            const selectedLedgerData = apiLedgers.find(l => l.id === selectedLedger?.id);
            if (!selectedLedgerData) {
                notify("danger", "Ledger not found");
                return;
            }

            const payload = {
                publisherID: publisherDetails.ID,
                month: getFirstDayOfMonth(publisherDetails.month),
                adjustmentType: adjustmentForm.type,
                amount: parseFloat(adjustmentForm.amount),
                description: adjustmentForm.description,
                createdBy: currentUser,
            };
            const response = await addAdjustmentData(payload);
            if (response?.code === 200) {
                const amount = parseFloat(adjustmentForm.amount) * (adjustmentForm.type === "CREDIT" || adjustmentForm.type === "BONUS" ? 1 : -1);

                setApiLedgers(prev =>
                    prev.map(l => {
                        if (l.id === selectedLedgerData.id) {
                            const newAdjustmentList = [...l.adjustmentsList];
                            if (isEditMode && editingAdjustmentId) {
                                const index = newAdjustmentList.findIndex(a => a.id === editingAdjustmentId);
                                if (index !== -1) {
                                    newAdjustmentList[index] = {
                                        ...newAdjustmentList[index],
                                        type: adjustmentForm.type,
                                        amount: parseFloat(adjustmentForm.amount),
                                        description: adjustmentForm.description,
                                    };
                                }
                            } else {
                                newAdjustmentList.push({
                                    id: response.data?.id || Date.now(),
                                    type: adjustmentForm.type,
                                    amount: parseFloat(adjustmentForm.amount),
                                    description: adjustmentForm.description,
                                    createdAt: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
                                    user: currentUser,
                                });
                            }

                            return {
                                ...l,
                                adjustments: l.adjustments + amount,
                                adjustmentType: l.adjustmentType ? `${l.adjustmentType}, ${adjustmentForm.type}` : adjustmentForm.type,
                                adjustmentDescription: adjustmentForm.description,
                                adjustmentsList: newAdjustmentList,
                                netPayable: l.netPayable,
                                adjustmentID: response.data?.id || l.adjustmentID,
                            };
                        }
                        return l;
                    })
                );

                notify("success", `Adjustment of $${adjustmentForm.amount} ${isEditMode ? 'updated' : 'added'} successfully!`);

                await fetchLedgerData(pageNum);
                resetAdjustmentForm();
                setIsAdjustmentModalOpen(false);
            } else {
                notify("danger", response?.message || `Failed to ${isEditMode ? 'update' : 'add'} adjustment`);
            }
        } catch (error) {
            console.error("Error adding adjustment:", error);
            notify("danger", "Something went wrong while adding adjustment");
        } finally {
            setIsLoading(false);
        }
    };

    const handleMonthChange = (date) => {
        const selectedDate = date || new Date();
        const month = date ? String(date.getMonth() + 1) : "";
        const year = selectedDate.getFullYear();

        setCurrentYear(year);
        setSelectedMonth(month);
        setSelectedPublisher(""); // Clear publisher filter when month changes
        setPageNum(1);
        fetchLedgerData(1, month ? parseInt(month) : null, year, showPostLedger, "");
    };

    const handlePageChange = (page) => {
        setPageNum(page);
        fetchLedgerData(page);
    };

    const handlePostLedger = async (row) => {
        const userData = JSON.parse(localStorage.getItem("p"));
        const currentUser = userData?.email || userData?.role || "Admin";
        const openingBalance = 0;
        const closingBalance = openingBalance + (row.revenue || 0) + (row.adjustments || 0) - (row.advanceApplied || 0);

        const payload = {
            publisherID: row.publisherId,
            ledgerMonth: row.month,
            adjustmentID: row.adjustmentID || 0,
            openingBalance: openingBalance,
            revenue: row.revenue || 0,
            adjustmentTotal: row.adjustments || 0,
            advanceApplied: row.advanceApplied || 0,
            closingBalance: closingBalance,
            netPayable: row.netPayable,
            currency: row.currency || "USD",
            status: "POSTED",
            postedBy: currentUser,
        };
        try {
            const response = await postLedger(payload);
            if (response?.code === 200) {
                notify("success", "Ledger posted successfully!");
                setApiLedgers(prev =>
                    prev.map(l => {
                        if (l.id === row.id) {
                            return { ...l, status: "POSTED" };
                        }
                        return l;
                    })
                );
                await fetchLedgerData(pageNum);
            } else {
                const message = Array.isArray(response?.message)
                    ? response?.message[0]?.Msg
                    : response?.message || "Failed to post ledger";
                notify("danger", message);
            }
        } catch (error) {
            console.error("Error posting ledger:", error);
            notify("danger", "Something went wrong while posting ledger");
        }
    };

    const handleBulkPostLedger = () => {
        setIsLoading(true);

        let openLedgers = [];
        if (selectedPublisher) {
            // If publisher is selected, post all open ledgers for that publisher
            openLedgers = apiLedgers.filter(l =>
                l.publisherId == selectedPublisher &&
                l.status === "open"
            );
        } else {
            // Otherwise post for the selected month
            const monthKey = `${currentYear}-${String(selectedMonth).padStart(2, "0")}`;
            openLedgers = apiLedgers.filter(l =>
                l.month === monthKey &&
                l.status === "open"
            );
        }

        if (openLedgers.length === 0) {
            notify("success", "No open ledgers to post");
            setIsLoading(false);
            return;
        }

        const postPromises = openLedgers.map(async (ledger) => {
            const userData = JSON.parse(localStorage.getItem("p"));
            const currentUser = userData?.email || userData?.role || "Admin";

            let netPayable = ledger.netPayable;

            const openingBalance = 0;
            const closingBalance = openingBalance + (ledger.revenue || 0) + (ledger.adjustments || 0) - (ledger.advanceApplied || 0);

            const payload = {
                publisherID: ledger.publisherId,
                ledgerMonth: ledger.month,
                adjustmentID: ledger.adjustmentID || 0,
                openingBalance: openingBalance,
                revenue: ledger.revenue || 0,
                adjustmentTotal: ledger.adjustments || 0,
                advanceApplied: ledger.advanceApplied || 0,
                closingBalance: closingBalance,
                netPayable: netPayable,
                currency: ledger.currency || "USD",
                status: "POSTED",
                postedBy: currentUser,
            };

            try {
                const response = await postLedger(payload);
                if (response?.code === 200) {
                    return { success: true, id: ledger.id };
                } else {
                    return { success: false, id: ledger.id, error: response?.message };
                }
            } catch (error) {
                return { success: false, id: ledger.id, error: error.message };
            }
        });

        Promise.all(postPromises).then((results) => {
            const successCount = results.filter(r => r.success).length;
            const failCount = results.filter(r => !r.success).length;

            if (successCount > 0) {
                setApiLedgers(prev =>
                    prev.map(l => {
                        if (results.some(r => r.success && r.id === l.id)) {
                            return { ...l, status: "POSTED" };
                        }
                        return l;
                    })
                );
                notify("success", `${successCount} ledgers posted successfully!`);
                if (failCount > 0) {
                    notify("danger", `${failCount} ledgers failed to post`);
                }
                fetchLedgerData(pageNum);
            } else {
                notify("danger", results[0].error);
            }
            setIsLoading(false);
        }).catch((error) => {
            console.error("Error in bulk post:", error);
            notify("danger", "Something went wrong while posting ledgers");
            setIsLoading(false);
        });
    };

    const handleAddAdvance = () => {
        if (!advanceForm.amount || !advanceForm.description) return;
        notify("success", `Advance payment of $${advanceForm.amount} added successfully!`);
        setIsAdvanceModalOpen(false);
        setAdvanceForm({ amount: "", currency: "USD", date: new Date(), description: "" });
    };

    const handleRerun = async (ledgerData) => {
        if (!reason || reason.trim() === "") {
            setRerunErrors({ reason: "Reason is required" });
            return;
        }

        setRerunErrors({});

        const userData = JSON.parse(localStorage.getItem("p"));
        const payload = {
            status: "RERUN",
            reason: reason,
            rerunBy: userData?.role || "SUPER_ADMIN"
        };
        const res = await updateLedger(ledgerData.ledgerID, payload);
        if (res?.code === 200) {
            notify("success", "Ledger rerun successfully!");
            await fetchLedgerData(pageNum);
        } else {
            const message = Array.isArray(res?.message)
                ? res?.message[0]?.Msg
                : res?.message || "Failed to rerun ledger";
            notify("danger", message);
        }
        setIsRerunModalOpen(false);
    };

    const handleBulkRerunAll = () => {
        let ledgersToRerun = [];
        if (!reason || reason.trim() === "") {
            setRerunErrors({ reason: "Reason is required" });
            return;
        }
        if (selectedPublisher) {
            // If publisher is selected, rerun all ledgers for that publisher
            ledgersToRerun = apiLedgers.filter(l =>
                l.publisherId == selectedPublisher && l.ledgerID
            );
        } else {
            // Otherwise rerun for the selected month
            const monthKey = `${currentYear}-${String(selectedMonth).padStart(2, "0")}`;
            ledgersToRerun = apiLedgers.filter(l =>
                l.month === monthKey && l.ledgerID
            );
        }

        if (!ledgersToRerun.length) {
            notify("danger", "No ledgers to rerun for the selected filters");
            return;
        }

        showAlert({
            title: "Are you sure you want to rerun all ledgers?",
            message: `This will rerun ${ledgersToRerun.length} ledger(s).`,
            type: "warning",
            confirmStyle: "secondary",
            cancelStyle: "btn btn-primary btn-gradient-primary",
            confirmText: "Yes",
            cancelText: "Cancel",
            onConfirm: async () => {
                setIsLoading(true);
                const results = await Promise.all(ledgersToRerun.map(async (ledger) => {
                    const payload = {
                        status: "RERUN",
                        reason: reason,
                        rerunBy: userData?.role || "SUPER_ADMIN"
                    };
                    const res = await updateLedger(ledger.ledgerID, payload);
                    setIsRerunModalOpen(false);
                    return { success: res?.code === 200, ledgerId: ledger.ledgerID, error: res.message };
                }));

                const successCount = results.filter(r => r.success).length;
                const failCount = results.filter(r => !r.success).length;

                if (successCount > 0) {
                    notify("success", `${successCount} ledgers rerun successfully!`);
                }
                if (failCount > 0) {
                    notify("danger", results[0].error);
                }
                console.log(results, "results")
                await fetchLedgerData(pageNum);
                setIsLoading(false);
            },
            onCancel: () => { },
        });
    };

    const handleBulkLockAll = () => {
        let ledgersToLock = [];
        if (selectedPublisher) {
            // If publisher is selected, lock all ledgers for that publisher
            ledgersToLock = apiLedgers.filter(l =>
                l.publisherId == selectedPublisher &&
                l.ledgerID &&
                l.status !== "LOCK" &&
                l.status !== "PAID"
            );
        } else {
            // Otherwise lock for the selected month
            const monthKey = `${currentYear}-${String(selectedMonth).padStart(2, "0")}`;
            ledgersToLock = apiLedgers.filter(l =>
                l.month === monthKey &&
                l.ledgerID &&
                l.status !== "LOCK" &&
                l.status !== "PAID"
            );
        }

        if (!ledgersToLock.length) {
            notify("danger", "No ledgers to lock for the selected filters");
            return;
        }

        showAlert({
            title: "Are you sure you want to lock all ledgers?",
            message: `This will lock ${ledgersToLock.length} ledger(s).`,
            type: "info",
            confirmStyle: "secondary",
            cancelStyle: "btn btn-primary btn-gradient-primary",
            confirmText: "Yes",
            cancelText: "Cancel",
            onConfirm: async () => {
                setIsLoading(true);
                const results = await Promise.all(ledgersToLock.map(async (ledger) => {
                    const res = await updateLedger(ledger.ledgerID, { status: "LOCK" });
                    return { success: res?.code === 200, ledgerId: ledger.ledgerID };
                }));

                const successCount = results.filter(r => r.success).length;
                const failCount = results.filter(r => !r.success).length;

                if (successCount > 0) {
                    notify("success", `${successCount} ledgers locked successfully!`);
                }
                if (failCount > 0) {
                    notify("danger", `${failCount} ledgers failed to lock`);
                }

                await fetchLedgerData(pageNum);
                setIsLoading(false);
            },
            onCancel: () => { },
        });
    };

    const changeStatus = async (invoiceId, status) => {
        const payload = {
            status: status
        };
        const res = await updateInvoiceStatus(invoiceId, payload);
        if (res?.code === 200) {
            notify("success", "Ledger updated successfully!");
            await fetchLedgerData(pageNum);
        } else {
            const message = Array.isArray(res?.message)
                ? res?.message[0]?.Msg
                : res?.message || "Failed to update ledger";
            notify("danger", message);
        }
    };

    const handleExportPDF = () => {
        notify("info", "Exporting PDF invoices... (ZIP download in production)");
    };

    const handleExportMIS = () => {
        notify("info", "Exporting Excel MIS... (Excel download in production)");
    };

    const handleSizePerPageChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setPageNum(1);
        fetchLedgerData(1);
    };

    const togglePublisherSelection = (publisherId) => {
        setSelectedPublishers(prev =>
            prev.includes(publisherId)
                ? prev.filter(id => id !== publisherId)
                : [...prev, publisherId]
        );
    };

    const selectAllPublishers = () => {
        const allIds = filteredLedgers.map(l => l.publisherId);
        setSelectedPublishers(allIds);
    };

    const handleReset = () => {
        setSelectedPublisher("");
        setSelectedMonth(getInitialMonth());
        setFilterText("");
        setPageNum(1);
        setSelectedPublishers([]);
        // Reset to current month view
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();
        setCurrentYear(currentYear);
        setSelectedMonth(String(currentMonth));
        fetchLedgerData(1, currentMonth, currentYear, showPostLedger, "");
    };

    const toggleView = () => {
        const newShowPostLedger = !showPostLedger;
        setShowPostLedger(newShowPostLedger);
        setSelectedPublishers([]);
        setPageNum(1);
        // When switching to post ledger view, clear publisher filter
        if (newShowPostLedger) {
            setSelectedPublisher("");
            // Use selected month or current month
            const month = selectedMonth ? parseInt(selectedMonth) : new Date().getMonth() + 1;
            const year = selectedMonth ? currentYear : new Date().getFullYear();
            fetchLedgerData(1, month, year, newShowPostLedger, "");
        } else {
            // When switching back to ledger view, maintain filters
            const month = selectedMonth ? parseInt(selectedMonth) : null;
            const year = selectedMonth ? currentYear : null;
            fetchLedgerData(1, month, year, newShowPostLedger, selectedPublisher);
        }
    };

    const handleRowClick = (row) => {
        setSelectedPublishers((prev) =>
            prev.includes(row.publisherId)
                ? prev.filter(id => id !== row.publisherId)
                : [...prev, row.publisherId]
        );
    };

    const openAdjustmentModal = async (row) => {
        setSelectedLedger(row);
        setPublisherDetails({
            ID: row.publisherId,
            name: row.publisherName,
            month: parseInt(selectedMonth)
        });

        const adjustmentId = row.adjustmentID || 0;
        if (adjustmentId > 0) {
            await fetchAdjustmentDetails(adjustmentId);
        } else {
            resetAdjustmentForm();
            setPublisherDetails({
                ID: row.publisherId,
                name: row.publisherName,
                month: parseInt(selectedMonth)
            });
        }

        setIsAdjustmentModalOpen(true);
    };

    const downloadFile = (blobData, fileName, contentType) => {
        const blob = new Blob([blobData], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = window.document.createElement("a");

        link.href = url;
        link.download = fileName;

        window.document.body.appendChild(link);
        link.click();

        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleDownloadInvoice = async (row) => {
        console.log(row);
        try {
            const res = await downloadInvoice(row.invoiceId);

            // Check if response is an error (JSON object with code/message)
            if (res?.code && (res?.code !== 200)) {
                const message = Array.isArray(res?.message)
                    ? res?.message[0]?.Msg
                    : res?.message || "Something went wrong";
                notify("danger", message);
                return;
            }

            // Determine filename and content type
            let fileName = row.invoiceNumber || "invoice";
            let contentType = "application/octet-stream";

            // Try to get file extension from the invoice path/url
            const invoicePath = row.invoicePath || row.InvoicePath || "";
            if (invoicePath) {
                const pathParts = invoicePath.split("/");
                const fullFileName = pathParts[pathParts.length - 1]; // e.g. "3_testing.png"

                if (fullFileName) {
                    // Extract the extension from the path
                    const extension = fullFileName.split(".").pop().toLowerCase();
                    fileName = `${fileName}.${extension}`;

                    // Determine content type based on extension
                    switch (extension) {
                        case "pdf":
                            contentType = "application/pdf";
                            break;
                        case "png":
                            contentType = "image/png";
                            break;
                        case "jpg":
                        case "jpeg":
                            contentType = "image/jpeg";
                            break;
                        case "gif":
                            contentType = "image/gif";
                            break;
                        case "doc":
                            contentType = "application/msword";
                            break;
                        case "docx":
                            contentType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                            break;
                        default:
                            contentType = "application/octet-stream";
                    }
                }
            }

            // If response is binary (Blob or ArrayBuffer), download it
            if (res instanceof Blob || res instanceof ArrayBuffer || typeof res === "string") {
                downloadFile(res, fileName, contentType);
                notify("success", `Downloading ${fileName}`);
            } else {
                notify("danger", "Invalid file response");
            }
        } catch (error) {
            console.error("Download error:", error);
            notify("danger", "Failed to download invoice");
        }
    };

    // =============== RENDER HELPERS ===============
    const getStatusBadge = (status) => {
        const statusMap = {
            open: { color: "warning", label: "Open" },
            LOCK: { color: "primary", label: "Locked" },
            PAID: { color: "primary", label: "Locked" },
            DRAFT: { color: "secondary", label: "Draft" },
            POSTED: { color: "info", label: "Posted" },
            INVOICED: { color: "primary", label: "Invoiced" },
            RERUN: { color: "danger", label: "Rerun" },
        };
        const s = statusMap[status] || statusMap.open;
        return <Badge color={s.color}>{s.label}</Badge>;
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null || isNaN(amount)) {
            return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(0);
        }
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
    };

    const buttonClick = ()=>{
        setIsLedgerManagementModal(!isLedgerManagementModal)
    }

    // =============== MEMOIZED COLUMNS ===============
    const columns = useMemo(() => [
        {
            dataField: "publisherName",
            text: "Publisher",
            formatter: (cell, row) => (
                <div>
                    <strong>{row.publisherName}</strong>
                    <br />
                    <small className="text-muted">{row.country}</small>
                </div>
            ),
            sort: true,
        },
        {
            dataField: "monthDisplay",
            text: "Month",
            formatter: (cell) => cell || "-",
            sort: true,
        },
        {
            dataField: "revenue",
            text: "Publisher Payout",
            formatter: (cell) => formatCurrency(cell),
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "adjustments",
            text: "Adjustments",
            formatter: (cell, row) => {
                const adjustmentType = row.adjustmentType || "";
                const isCredit = adjustmentType === "CREDIT" || adjustmentType === "BONUS";
                const isDebit = adjustmentType === "DEBIT" || adjustmentType === "DEDUCTION";

                if (cell === 0) return "-";

                return (
                    <span className={isCredit ? "text-danger" : isDebit ? "text-success" : cell > 0 ? "text-danger" : "text-success"}>
                        {formatCurrency(cell)}
                    </span>
                );
            },
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "adjustmentDescription",
            text: "Description",
            formatter: (cell) => {
                if (!cell) return <span className="text-muted">No description</span>;
                const truncated = truncateText(cell, 30);

                if (truncated === cell) {
                    return <span>{cell}</span>;
                }

                const uniqueId = `desc-${Math.random().toString(36).substr(2, 9)}`;
                return (
                    <>
                        <span
                            id={uniqueId}
                            style={{ cursor: 'pointer', borderBottom: '1px dashed #ccc' }}
                        >
                            {truncated}
                        </span>
                        <UncontrolledTooltip delay={0} target={uniqueId}>
                            {cell}
                        </UncontrolledTooltip>
                    </>
                );
            },
            sort: true,
            headerAlign: "left",
            align: "left",
        },
        {
            dataField: "netPayable",
            text: "Net Payout",
            formatter: (cell) => {
                if (cell === undefined || cell === null) return "-";
                const value = parseFloat(cell);
                if (isNaN(value)) return "-";
                return <span className="fw-bold">{formatCurrency(value)}</span>;
            },
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "advanceApplied",
            text: "Advance Applied",
            formatter: (cell) => formatCurrency(cell),
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "totalPayout",
            text: "Final Payout",
            formatter: (cell) => {
                if (cell === undefined || cell === null) return "-";
                const value = parseFloat(cell);
                if (isNaN(value)) return "-";
                return <span className="fw-bold">{formatCurrency(value)}</span>;
            },
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "invoiceNumber",
            text: "Invoice Number",
            formatter: (cell, row) => {
                if (row.invoiceNumber && row.invoiceNumber !== "") {
                    return (
                        <a
                            href="#"
                            style={{ color: '#1a3c6e', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedLedger(row);
                                setIsInvoiceModalOpen(true);
                            }}
                        >
                            {row.invoiceNumber}
                        </a>
                    );
                }
                return <span className="text-muted">NA</span>;
            },
            sort: true,
        },
        {
            dataField: "invoiceStatus",
            text: "Invoice Status",
            formatter: (cell, row) => {
                if (row.invoiceStatus && row.invoiceStatus !== "") {
                    const statusMap = {
                        OPEN: { color: "warning", label: "Open" },
                        ACCEPTED: { color: "success", label: "Accepted" },
                        PAID: { color: "success", label: "Paid" },
                        VOID: { color: "danger", label: "Void" },
                        PENDING: { color: "warning", label: "Pending" },
                    };
                    const s = statusMap[row.invoiceStatus] || statusMap.OPEN;
                    return <Badge color={s.color}>{s.label}</Badge>;
                }
                return <span className="text-muted">No invoice</span>;
            },
            sort: true,
        },
        {
            dataField: "status",
            text: "Status",
            formatter: (cell) => getStatusBadge(cell),
            sort: true,
        },
        {
            dataField: "postedAt",
            text: "Posted At",
            formatter: (cell) => cell || "-",
            sort: true,
        },
        {
            dataField: "actions",
            text: "Actions",
            formatter: (cell, row) => {
                const rowId = row.id;
                return (
                    <div >
                        {row.invoiceStatus !== "PAID" && row.status !== "RERUN" && row.totalPayout > 0 && row.invoiceStatus !== "ACCEPTED" && (
                            <>
                                <a
                                    className="table-action table-action-primary"
                                    id={`upload-${rowId}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedLedger(row);
                                        setUploadInvoiceForm({
                                            publisherName: row.publisherName || "",
                                            monthDisplay: row.monthDisplay || row.month || "",
                                            invoiceNumber: row.invoiceNumber || "",
                                            invoiceFile: null,
                                        });
                                        setIsUploadInvoiceModalOpen(true);
                                    }}
                                >
                                    <i className="fas fa-upload" />
                                </a>
                                <UncontrolledTooltip delay={0} target={`upload-${rowId}`}>
                                    Upload Invoice
                                </UncontrolledTooltip>
                            </>
                        )}
                        {row.status === "LOCK" && row.invoiceNumber && row.invoiceStatus === "ACCEPTED" && (
                            <>
                                {console.log(row)}
                                <a
                                    className="table-action table-action-info"
                                    id={`paid-${rowId}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        showAlert({
                                            title: `Are you sure you want to Mark as paid to this Ledger?`,
                                            message: `Ledger for "${row?.publisherName}" will be Paid.`,
                                            type: "success",
                                            confirmStyle: "secondary",
                                            cancelStyle: "btn btn-primary btn-gradient-primary",
                                            confirmText: "Yes",
                                            cancelText: "Cancel",
                                            onConfirm: () => changeStatus(row.invoiceId, "PAID"),
                                            onCancel: () => { },
                                        });
                                    }}
                                >
                                    <i className="fa-solid fa-check" />
                                </a>
                                <UncontrolledTooltip delay={0} target={`paid-${rowId}`}>
                                    Mark as Paid
                                </UncontrolledTooltip>
                            </>
                        )}
                        {row.invoiceNumber && (
                            <>
                                <a
                                    className="table-action table-action-info"
                                    id={`view-${rowId}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedLedger(row);
                                        setIsInvoiceModalOpen(true);
                                    }}
                                >
                                    <i className="fas fa-eye" />
                                </a>
                                <UncontrolledTooltip delay={0} target={`view-${rowId}`}>
                                    View Invoice
                                </UncontrolledTooltip>
                            </>
                        )}
                        {row.invoiceNumber && (
                            <>
                                <a
                                    className="table-action table-action-info"
                                    id={`download-${rowId}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadInvoice(row)
                                    }}
                                >
                                    <i className="fas fa-download" />
                                </a>
                                <UncontrolledTooltip delay={0} target={`download-${rowId}`}>
                                    Download Invoice
                                </UncontrolledTooltip>
                            </>
                        )}
                    </div>
                );
            },
            headerAlign: "center",
            align: "center",
        }
    ], [changeStatus, notify, userData]);

    // =============== POST LEDGER TABLE COLUMNS ===============
    const postLedgerColumns = useMemo(() => [
        {
            dataField: "publisherName",
            text: "Publisher",
            formatter: (cell, row) => (
                <div>
                    <strong>{row.publisherName}</strong>
                    <br />
                    <small className="text-muted">{row.country}</small>
                </div>
            ),
            sort: true,
        },
        {
            dataField: "monthDisplay",
            text: "Month",
            formatter: (cell) => cell || "-",
            sort: true,
        },

        {
            dataField: "revenue",
            text: "Publisher Payout",
            formatter: (cell) => formatCurrency(cell),
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "adjustments",
            text: "Adjustments",
            formatter: (cell, row) => {
                const adjustmentType = row.adjustmentType || "";
                const isCredit = adjustmentType === "CREDIT" || adjustmentType === "BONUS";
                const isDebit = adjustmentType === "DEBIT" || adjustmentType === "DEDUCTION";

                if (cell === 0) return "-";

                return (
                    <span className={isCredit ? "text-danger" : isDebit ? "text-success" : cell > 0 ? "text-danger" : "text-success"}>
                        {formatCurrency(cell)}
                    </span>
                );
            },
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "adjustmentDescription",
            text: "Description",
            formatter: (cell) => {
                if (!cell) return <span className="text-muted">No description</span>;
                const truncated = truncateText(cell, 30);

                if (truncated === cell) {
                    return <span>{cell}</span>;
                }

                const uniqueId = `desc-${Math.random().toString(36).substr(2, 9)}`;
                return (
                    <>
                        <span
                            id={uniqueId}
                            style={{ cursor: 'pointer', borderBottom: '1px dashed #ccc' }}
                        >
                            {truncated}
                        </span>
                        <UncontrolledTooltip delay={0} target={uniqueId}>
                            {cell}
                        </UncontrolledTooltip>
                    </>
                );
            },
            sort: true,
            headerAlign: "left",
            align: "left",
        },
        {
            dataField: "netPayable",
            text: "Net Payaout",
            formatter: (cell, row) => {
                return <span className="fw-bold">{formatCurrency(row.netPayable)}</span>;
            },
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "advanceApplied",
            text: "Advance Applied",
            formatter: (cell) => formatCurrency(cell),
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "totalPayout",
            text: "Final Payout",
            formatter: (cell) => {
                if (cell === undefined || cell === null) return "-";
                const value = parseFloat(cell);
                if (isNaN(value)) return "-";
                return <span className="fw-bold">{formatCurrency(value)}</span>;
            },
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "status",
            text: "Status",
            formatter: (cell) => getStatusBadge(cell),
            sort: true,
            headerAlign: "center",
            align: "center"
        },
        {
            dataField: "actions",
            text: "Actions",
            formatter: (cell, row) => {
                const rowId = row.id;
                const hasAdjustment = row.adjustmentID && row.adjustmentID > 0;
                const isPosted = row.status === "POSTED" || row.status === "LOCK" || row.status === "PAID" || row.status === "INVOICED";
                return (
                    <div >
                        {(!isPosted) && (
                            <>
                                <a
                                    className="table-action table-action-info"
                                    id={`post-adjust-${rowId}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openAdjustmentModal(row);
                                    }}
                                >
                                    <i className="fas fa-edit" />
                                </a>
                                <UncontrolledTooltip delay={0} target={`post-adjust-${rowId}`}>
                                    {hasAdjustment ? "Edit Adjustment" : "Add Adjustment"}
                                </UncontrolledTooltip>
                            </>
                        )}


                        {isPosted && (
                            <span className="text-success">
                                <i className="fas fa-check-circle" /> Posted
                            </span>
                        )}
                    </div>
                );
            },
            headerAlign: "center",
            align: "center",
        }
    ], [isLoading, openAdjustmentModal, handlePostLedger]);

    // =============== FILTER SECTION ===============
    const filterSection = () => (
        <Row className="align-items-center">
            <Col md={3}>
                <FormGroup>
                    <DatePicker
                        selected={selectedMonth ? new Date(currentYear, Number(selectedMonth) - 1, 1) : null}
                        onChange={handleMonthChange}
                        className="form-control"
                        calendarClassName="styled-month-picker"
                        popperClassName="styled-month-picker-popper"
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        placeholderText="Select Month / Year"
                        // isClearable
                        disabled={!!selectedPublisher}
                    />
                </FormGroup>
            </Col>
            {!showPostLedger && (

                <Col md={3}>
                    <FormGroup>
                        <Input
                            type="select"
                            value={selectedPublisher}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSelectedPublisher(val);
                                setPageNum(1);
                                if (val) {
                                    // Clear month when publisher is selected
                                    setSelectedMonth("");
                                    fetchLedgerData(1, null, null, showPostLedger, val);
                                } else {
                                    // All publishers selected — use currently selected month/year
                                    const month = selectedMonth ? parseInt(selectedMonth) : new Date().getMonth() + 1;
                                    const year = selectedMonth ? currentYear : new Date().getFullYear();
                                    fetchLedgerData(1, month, year, showPostLedger, "");
                                }
                            }}
                        >
                            <option value="">All Publishers</option>
                            {publisherOptions.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
            )}

            <Col md={showPostLedger ? 6 : 6} className="d-flex align-items-center gap-2 flex-wrap">
                {showPostLedger ? (
                    <>
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                showAlert({
                                    title: `Are you sure you want to Post All Ledgers?`,
                                    message: ``,
                                    type: "warning",
                                    confirmStyle: "secondary",
                                    cancelStyle: "btn btn-primary btn-gradient-primary",
                                    confirmText: "Yes",
                                    cancelText: "Cancel",
                                    onConfirm: () => handleBulkPostLedger(),
                                    onCancel: () => { },
                                });
                            }}
                            className="filter-btn-gradient-primary btn-md responsive"
                            disabled={isLoading}
                        >
                            {isLoading ? <Spinner size="sm" /> : <><FaCheck className="me-1" /> Post All</>}
                        </Button>
                        {hasRerun && (

                            <Button
                                color="warning"
                                outline
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowRerunLogModal(true);
                                }}
                                disabled={isLoading}
                            >
                                Rerun Logs
                            </Button>
                        )}
                        {totalPostedRecords > 0 && (

                            <Button
                                color="primary"
                                outline
                                onClick={handleBulkLockAll}
                                disabled={isLoading}
                            >
                                <FaLock className="me-1" /> Lock All
                            </Button>
                        )}

                    </>
                ) : apiLedgers.length > 0 && totalPostedRecords > 0 && !selectedPublisher && (
                    <>
                        <Button
                            color="primary"
                            outline
                            onClick={handleBulkLockAll}
                            disabled={isLoading}
                        >
                            <FaLock className="me-1" /> Lock All
                        </Button>
                        <Button
                            color="warning"
                            outline
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsRerunModalOpen(true);
                            }}
                            disabled={isLoading}
                        >
                            <FaHistory className="me-1" /> Rerun All
                        </Button>

                    </>
                )}
                {hasRerun && (

                    <Button
                        color="warning"
                        outline
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowRerunLogModal(true);
                        }}
                        disabled={isLoading}
                    >
                        Rerun Logs
                    </Button>
                )}
                <Button className="secondary" onClick={handleReset}>
                    Reset
                </Button>
            </Col>
        </Row>
    );

    // =============== RENDER ===============
    return (
        <>
            <style>{monthPickerStyles}</style>
            <div className="rna-container">
                <NotificationAlert ref={notificationAlertRef} />
            </div>
            <Container className="mt--6" fluid>
                <Card>
                    <ReactBsTable
                        title={showPostLedger ? "Post Ledger" : "Ledger & Invoicing"}
                        description=""
                        data={filteredLedgers}
                        columns={showPostLedger ? postLedgerColumns : columns}
                        keyField="id"
                        key={selectedPublishers.join(",")}
                        renderFilters={filterSection}
                        page={pageNum}
                        sizePerPage={pageSize}
                        exportCSVBtnShow={false}
                        onexportCsvBtnClick={() => setIsExportModalOpen(true)}
                        totalRecords={filteredLedgers.length}
                        onPageChange={handlePageChange}
                        showLoader={isLoading}
                        handleSizePerPageChange={handleSizePerPageChange}
                        enablePagination={false}
                        showColumnsBtn={showPostLedger}
                        addbtntext="Post Ledger"
                        showAddbtn={!showPostLedger}
                        onAddBtnClick={toggleView}
                        Columnstext={showPostLedger ? "Back to List" : "Post Ledger"}
                        toggleColumnsModal={toggleView}
                        bordered
                        hover
                        extraButton={true}
                        icon="fa fa-info-circle"
                        buttonText=""
                        buttonOnClick={buttonClick}
                    />
                </Card>

                {/* ========== ADJUSTMENT MODAL ========== */}
                <Modal isOpen={isAdjustmentModalOpen} toggle={() => {
                    setIsAdjustmentModalOpen(false);
                    resetAdjustmentForm();
                }} centered size="lg">
                    <ModalHeader toggle={() => {
                        setIsAdjustmentModalOpen(false);
                        resetAdjustmentForm();
                    }} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>
                            {isEditMode ? "Edit Adjustment" : "Add Adjustment"}
                        </h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>
                                            Publisher <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            disabled={true}
                                            type="text"
                                            value={publisherDetails.name || ""}
                                            readOnly
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md={6}>
                                    <FormGroup>
                                        <Label>
                                            Month <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            disabled={true}
                                            type="text"
                                            value={publisherDetails.month ? `${MONTHS.find(m => m.value === String(publisherDetails.month))?.label || ""}` : ""}
                                            readOnly
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>
                                            Adjustment Type <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="select"
                                            name="type"
                                            value={adjustmentForm.type || "CREDIT"}
                                            onChange={(e) => {
                                                setAdjustmentForm({ ...adjustmentForm, type: e.target.value });
                                                setAdjustmentErrors({ ...adjustmentErrors, type: "" });
                                            }}
                                            invalid={!!adjustmentErrors.type}
                                        >
                                            <option value="CREDIT">Positive (+)</option>
                                            <option value="DEBIT">Negative (-)</option>
                                        </Input>
                                        {adjustmentErrors.type && (
                                            <div className="text-danger small mt-1">{adjustmentErrors.type}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>
                                            Value <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="number"
                                            name="amount"
                                            placeholder="Enter Value"
                                            value={adjustmentForm.amount}
                                            onChange={(e) => {
                                                setAdjustmentForm({ ...adjustmentForm, amount: e.target.value });
                                                setAdjustmentErrors({ ...adjustmentErrors, amount: "" });
                                            }}
                                            invalid={!!adjustmentErrors.amount}
                                            min="0.01"
                                            step="0.01"
                                        />
                                        {adjustmentErrors.amount && (
                                            <div className="text-danger small mt-1">{adjustmentErrors.amount}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={12}>
                                    <FormGroup>
                                        <Label>
                                            Reason <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="textarea"
                                            name="description"
                                            placeholder="Enter Reason"
                                            value={adjustmentForm.description}
                                            onChange={(e) => {
                                                setAdjustmentForm({ ...adjustmentForm, description: e.target.value });
                                                setAdjustmentErrors({ ...adjustmentErrors, description: "" });
                                            }}
                                            invalid={!!adjustmentErrors.description}
                                            rows={3}
                                        />
                                        {adjustmentErrors.description && (
                                            <div className="text-danger small mt-1">{adjustmentErrors.description}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => {
                            setIsAdjustmentModalOpen(false);
                            resetAdjustmentForm();
                        }}>
                            Cancel
                        </Button>
                        <Button
                            className="filter-btn-gradient-primary btn-md responsive"
                            onClick={handleAddAdjustment}
                            disabled={isLoading}
                        >
                            {isLoading ? <><Spinner size="sm" className="me-1" /> Saving...</> : (isEditMode ? "Update Adjustment" : "Add Adjustment")}
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Invoice Preview Modal */}
                <Modal isOpen={isLedgerManagementModal} toggle={() => setIsLedgerManagementModal(false)} centered size="xl">
                    <ModalHeader toggle={() => setIsLedgerManagementModal(false)} style={modalStyles.header}>
                        <h3 style={modalStyles.title}>Ledger Management Info</h3>
                    </ModalHeader>
                    <ModalBody style={{ ...modalStyles.body, overflow: 'hidden' }}>
                  
                            <div className="invoice-preview p-4">


                            
                                    <div style={{ height: '70vh', marginTop: 16, overflow: 'hidden' }}>
                                        <iframe
                                            src="/docs/Ledger Steps Guid.pdf"
                                            title="Invoice Preview"
                                            style={{ width: '100%', height: '100%', border: 'none' }}
                                        />
                                    </div>


                                {/* <Row className="mt-2">
                                    <Col>
                                        <p className="text-muted" style={{ fontSize: '11px', marginBottom: '0' }}>
                                            * This invoice is generated automatically by TrueLift system. VAT is charged at 5% for UAE supplies.
                                        </p>
                                    </Col>
                                </Row> */}
                            </div>
                        
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsLedgerManagementModal(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Invoice Preview Modal */}
                <Modal isOpen={isInvoiceModalOpen} toggle={() => setIsInvoiceModalOpen(false)} centered size="xl">
                    <ModalHeader toggle={() => setIsInvoiceModalOpen(false)} style={modalStyles.header}>
                        <h3 style={modalStyles.title}>Tax Invoice</h3>
                    </ModalHeader>
                    <ModalBody style={{ ...modalStyles.body, overflow: 'hidden' }}>
                        {selectedLedger && (
                            <div className="invoice-preview p-4">


                                {selectedLedger?.invoicePath && (
                                    <div style={{ height: '70vh', marginTop: 16, overflow: 'hidden' }}>
                                        <iframe
                                            src={selectedLedger.invoicePath}
                                            title="Invoice Preview"
                                            style={{ width: '100%', height: '100%', border: 'none' }}
                                        />
                                    </div>
                                )}

                                {/* <Row className="mt-2">
                                    <Col>
                                        <p className="text-muted" style={{ fontSize: '11px', marginBottom: '0' }}>
                                            * This invoice is generated automatically by TrueLift system. VAT is charged at 5% for UAE supplies.
                                        </p>
                                    </Col>
                                </Row> */}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsInvoiceModalOpen(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Upload Invoice Modal */}
                <Modal isOpen={isUploadInvoiceModalOpen} toggle={() => setIsUploadInvoiceModalOpen(false)} centered size="lg">
                    <ModalHeader toggle={() => setIsUploadInvoiceModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>Upload Invoice</h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Publisher</Label>
                                        <Input type="text" value={uploadInvoiceForm.publisherName} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Month</Label>
                                        <Input type="text" value={uploadInvoiceForm.monthDisplay} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <FormGroup>
                                <Label>Invoice Number <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    placeholder="Enter invoice number"
                                    value={uploadInvoiceForm.invoiceNumber}
                                    onChange={(e) => setUploadInvoiceForm({ ...uploadInvoiceForm, invoiceNumber: e.target.value })}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Upload Invoice File <span className="text-danger">*</span></Label>
                                <div
                                    className={`product-image-upload w-100 p-3 rounded text-center ${uploadInvoiceForm.error ? "border border-warning" : "border"}`}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDragEnter={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const file = e.dataTransfer.files[0];
                                        if (!file) return;

                                        const allowedTypes = [
                                            "application/pdf",
                                            "application/msword",
                                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                                            "image/png",
                                            "image/jpeg",
                                            "image/jpg",
                                            "image/gif",
                                        ];

                                        if (!allowedTypes.includes(file.type)) return;

                                        if (file.size > MAX_FILE_SIZE) {
                                            setUploadInvoiceForm((prev) => ({ ...prev, error: "File size should not exceed 10 MB" }));
                                            return;
                                        }

                                        setUploadInvoiceForm((prev) => ({ ...prev, invoiceFile: file, error: null }));
                                    }}
                                >
                                    {uploadInvoiceForm?.invoiceFile?.name ? (
                                        <div className="border rounded p-3">
                                            <div className="d-flex flex-column align-items-start">
                                                <div className="d-flex align-items-start">
                                                    {getDocTypeFromName(uploadInvoiceForm.invoiceFile.name) === "pdf" ? (
                                                        <i className="fa fa-file-pdf text-danger fa-4x mb-2 ml-4" />
                                                    ) : getDocTypeFromName(uploadInvoiceForm.invoiceFile.name) === "doc" ? (
                                                        <i className="fa fa-file-word text-primary fa-4x mb-2 ml-4" />
                                                    ) : (
                                                        <i className="fa fa-file-image text-info fa-4x mb-2 ml-4" />
                                                    )}

                                                    <span
                                                        className="text-danger pointer ml-2"
                                                        onClick={() => setUploadInvoiceForm((prev) => ({ ...prev, invoiceFile: null }))}
                                                    >
                                                        <i className="fa fa-times" />
                                                    </span>
                                                </div>

                                                <p className="mb-2">{uploadInvoiceForm.invoiceFile.name}</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column align-items-center">
                                            <i className="fa fa-cloud-upload fa-2x text-muted mb-2" />
                                            <p className="mb-1 text-muted">Click or drag document to upload</p>
                                            <Label for="uploadInvoiceFileInput" className="btn btn-simple btn-sm mt-2">Choose Document</Label>
                                        </div>
                                    )}

                                    <Input
                                        type="file"
                                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,image/*"
                                        className="d-none"
                                        id="uploadInvoiceFileInput"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            if (file.size > MAX_FILE_SIZE) {
                                                setUploadInvoiceForm((prev) => ({ ...prev, error: "File size should not exceed 10 MB" }));
                                                e.target.value = "";
                                                return;
                                            }
                                            setUploadInvoiceForm((prev) => ({ ...prev, invoiceFile: file, error: null }));
                                            e.target.value = "";
                                        }}
                                    />
                                </div>
                                {uploadInvoiceForm.error && (
                                    <small style={{ color: "#fb6340", marginTop: 5 }}>{uploadInvoiceForm.error}</small>
                                )}
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsUploadInvoiceModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            className="filter-btn-gradient-primary btn-md responsive"
                            onClick={async () => {
                                if (!uploadInvoiceForm.invoiceNumber) { notify("danger", "Please enter invoice number"); return; }
                                if (!uploadInvoiceForm.invoiceFile) { notify("danger", "Please select an invoice file"); return; }
                                try {
                                    setIsLoading(true);

                                    const formData = new FormData();


                                    formData.append("file", uploadInvoiceForm.invoiceFile);
                                    const invoiceData = {
                                        publisherID: selectedLedger?.publisherId || uploadInvoiceForm.publisherId || 0,
                                        ledgerID: selectedLedger?.ledgerID || selectedLedger?.ledgerId || 0,
                                        invoiceNumber: uploadInvoiceForm.invoiceNumber,
                                        status: "PENDING",

                                    };
                                    formData.append("invoiceData", JSON.stringify(invoiceData));


                                    const res = await uploadInvoice(formData);

                                    if (res && (res.code === 200 || res.success)) {
                                        notify("success", "Invoice uploaded successfully");

                                        const invoicePath = res.data?.invoicePath || res.data?.InvoicePath || res.data?.path || res.data?.filePath || null;

                                        setApiLedgers(prev => prev.map(l => {
                                            if (l.id === selectedLedger?.id) {
                                                return {
                                                    ...l,
                                                    invoiceNumber: uploadInvoiceForm.invoiceNumber,
                                                    invoicePath: invoicePath || l.invoicePath,
                                                    invoiceStatus: "OPEN",
                                                };
                                            }
                                            return l;
                                        }));
                                        fetchLedgerData(1, selectedMonth, currentYear, showPostLedger, "");
                                        setIsUploadInvoiceModalOpen(false);
                                    } else {
                                        const msg = res?.message || "Failed to upload invoice";
                                        notify("danger", msg);
                                    }
                                } catch (err) {
                                    console.error(err);
                                    notify("danger", "Failed to upload invoice");
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            disabled={!uploadInvoiceForm.invoiceNumber || !uploadInvoiceForm.invoiceFile}
                        >
                            {isLoading ? <><Spinner size="sm" className="me-1" /> Uploading...</> : "Upload Invoice"}
                        </Button>
                    </ModalFooter>
                </Modal>
                <Modal isOpen={showRerunLogModal} toggle={() => { setShowRerunLogModal(false) }} centered size="xl">
                    <ModalHeader toggle={() => {
                        setShowRerunLogModal(false);

                    }} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>Rerun Logs for {getMonthDisplay(selectedMonth, currentYear)}</h5>
                    </ModalHeader>


                    <RerunLogs selectedMonth={selectedMonth} selectedYear={currentYear} />

                    <ModalFooter>
                        <Button color="secondary" onClick={() => {
                            setShowRerunLogModal(false);

                        }}>
                            Cancel
                        </Button>

                    </ModalFooter>
                </Modal>

                {/* Rerun Modal */}
                <Modal isOpen={isRerunModalOpen} toggle={() => {
                    setIsRerunModalOpen(false);
                    setReason("");
                    setRerunErrors({});
                }} centered size="lg">
                    <ModalHeader toggle={() => {
                        setIsRerunModalOpen(false);
                        setReason("");
                        setRerunErrors({});
                    }} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>Rerun Ledger</h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        <Alert className="filter-btn-gradient-primary">
                            <FaExclamationTriangle className="me-2" />
                            <strong>Warning:</strong> This action will:
                            <ul className="mb-0 mt-1">
                                <li>Void the current invoice(s)</li>
                                <li>Unlock the stats for this publisher and month</li>
                                <li>Allow you to re-post with corrections</li>
                            </ul>
                        </Alert>
                        {selectedLedger && (
                            <p>
                                <strong>Publisher:</strong> {selectedLedger.publisherName}<br />
                                <strong>Month:</strong> {selectedLedger.monthDisplay}<br />
                                <strong>Current Net Payable:</strong> {formatCurrency(selectedLedger.netPayable)}
                            </p>
                        )}
                        <FormGroup>
                            <Label>Reason for Rerun <span className="text-danger">*</span></Label>
                            <Input
                                type="textarea"
                                placeholder="Explain why this ledger needs to be rerun..."
                                onChange={(e) => {
                                    setReason(e.target.value);
                                    if (rerunErrors.reason) {
                                        setRerunErrors({});
                                    }
                                }}
                                rows={3}
                                invalid={!!rerunErrors.reason}
                            />
                            {rerunErrors.reason && (
                                <div className="text-danger small mt-1">{rerunErrors.reason}</div>
                            )}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button className="filter-btn-gradient-primary btn-md responsive" onClick={() => {
                            setIsRerunModalOpen(false);
                            setReason("");
                            setRerunErrors({});
                        }}>
                            Cancel
                        </Button>
                        <Button
                            color="secondary"

                            onClick={() => handleBulkRerunAll()}
                        >
                            Confirm Rerun
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Export Modal */}
                <Modal isOpen={isExportModalOpen} toggle={() => setIsExportModalOpen(false)} centered size="lg">
                    <ModalHeader toggle={() => setIsExportModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>Export Options</h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        <p>Select export format for {MONTHS.find(m => m.value === selectedMonth)?.label || "selected"} {currentYear}</p>
                        <div className="d-grid gap-3">
                            <Button color="primary" onClick={handleExportPDF} className="py-3">
                                <FaFilePdf className="me-2" size={24} />
                                <div>
                                    <strong>Invoice PDF Dump</strong>
                                    <div className="small text-white">All invoices for the month in a ZIP file</div>
                                </div>
                            </Button>
                            <Button color="success" onClick={handleExportMIS} className="py-3">
                                <FaFileExcel className="me-2" size={24} />
                                <div>
                                    <strong>Excel MIS</strong>
                                    <div className="small text-white">Complete financial summary with all publisher details</div>
                                </div>
                            </Button>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsExportModalOpen(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </>
    );
};

export default LedgerManagement;